<script setup lang="ts">
/**
 * Account scheduled for deletion / restore (decision 16, fair-api acct journal § FE contract 16).
 * No Figma frame (design gap, `docs/release_journal/fe.md`): built from the auth screens
 * (`AuthScreenShell`, Forgot password's form, Link expired's layout).
 *
 * Two entries, one screen:
 *  - the 403 `account_pending_deletion` (formerly `account_scheduled_for_deletion`) of a login or of any bearer call → the date
 *    (`restore_available_until`) and "Send the e-mail again" (`/account/restore/resend`, always
 *    202 — the answer never tells whether the address has an account);
 *  - the e-mail link `/account/restore?token=` → Restore → `POST /account/restore` → sign in.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { FairButton } from '@/ui/primitives/FairButton'
import { useFairToast } from '@/ui/patterns/FairToast'
import { useAuthModal } from '@/features/auth/store'
import { isEmail } from '@/features/auth/model/passwordRules'
import AuthField from '@/features/auth/ui/parts/AuthField.vue'
import AuthScreenShell from '@/features/auth/ui/parts/AuthScreenShell.vue'
import {
  accountRestoreErrorOf,
  formatAccountDeadline,
  resendAccountRestore,
  restoreAccount,
  useAccountGateStore,
  type AccountRestoreError,
} from '@/features/account'

const { t, locale } = useI18n()
const toast = useFairToast()
const authModal = useAuthModal()
const gate = useAccountGateStore()

type Phase = 'scheduled' | 'confirm' | 'restored'
const phase = ref<Phase>(gate.restoreToken ? 'confirm' : 'scheduled')
const restoring = ref(false)
const restoreError = ref<AccountRestoreError | null>(null)

const date = computed(() => formatAccountDeadline(gate.restoreAvailableUntil, locale.value))

const title = computed(() => {
  if (phase.value === 'restored') return t('account.restore.restored_title')
  if (phase.value === 'confirm') return t('account.restore.token_title')
  return t('account.restore.title')
})
const description = computed(() => {
  if (phase.value === 'restored') return t('account.restore.restored_description')
  if (phase.value === 'confirm') return t('account.restore.token_description')
  return date.value ? t('account.restore.description', { date: date.value }) : t('account.restore.description_no_date')
})

async function restore() {
  const token = gate.restoreToken
  if (!token || restoring.value) return
  restoring.value = true
  restoreError.value = null
  try {
    await restoreAccount(token)
    gate.setRestoreToken(null)
    phase.value = 'restored'
  } catch (error) {
    restoreError.value = accountRestoreErrorOf(error)
    if (restoreError.value === 'invalid_restore_token') gate.setRestoreToken(null)
  } finally {
    restoring.value = false
  }
}

/* ── Send the e-mail again ─────────────────────────────────────────────── */

const email = ref('')
const emailTouched = ref(false)
const sending = ref(false)
const emailInvalid = computed(() => emailTouched.value && email.value.trim() !== '' && !isEmail(email.value))
const showResend = computed(
  () => phase.value === 'scheduled' || restoreError.value === 'invalid_restore_token',
)

async function resend() {
  if (!isEmail(email.value) || sending.value) return
  sending.value = true
  try {
    await resendAccountRestore(email.value)
    toast.show({ tone: 'success', message: t('account.restore.resend_sent'), life: 5000 })
  } catch (error) {
    console.warn('Account restore: the e-mail was not re-sent', error)
    toast.show({ tone: 'warning', message: t('account.restore.resend_failed') })
  } finally {
    sending.value = false
  }
}
</script>

<template>
  <AuthScreenShell
    :centered="phase !== 'scheduled'"
    :title="title"
    :description="description"
  >
    <div
      class="fair-auth-form"
      data-testid="account-restore"
      :data-phase="phase"
    >
      <p
        v-if="restoreError"
        class="fair-auth-form__error"
        role="alert"
        data-testid="account-restore-error"
      >
        {{ t(`account.restore.errors.${restoreError}`) }}
      </p>

      <FairButton
        v-if="phase === 'confirm' && restoreError !== 'invalid_restore_token' && restoreError !== 'restore_expired'"
        class="fair-auth-cta"
        size="auto"
        :label="t('account.restore.restore')"
        :loading="restoring"
        data-testid="account-restore-submit"
        @click="restore"
      />

      <FairButton
        v-if="phase === 'restored' || restoreError === 'restore_not_available'"
        class="fair-auth-cta"
        size="auto"
        :label="t('account.restore.sign_in')"
        data-testid="account-restore-sign-in"
        @click="authModal.showAuthModal('login')"
      />

      <form
        v-if="showResend"
        class="fair-auth-form"
        novalidate
        @submit.prevent="resend"
      >
        <AuthField
          v-model="email"
          input-id="account-restore-email"
          type="email"
          autocomplete="email"
          :label="t('account.restore.resend_label')"
          :invalid="emailInvalid"
          :error-text="emailInvalid ? t('auth.errors.invalid_email') : undefined"
          @blur="emailTouched = true"
        />
        <FairButton
          class="fair-auth-cta"
          size="auto"
          type="submit"
          variant="tonal"
          :label="t('account.restore.resend')"
          :loading="sending"
          :disabled="!isEmail(email)"
          data-testid="account-restore-resend"
        />
      </form>
    </div>

    <p
      v-if="phase !== 'restored'"
      class="fair-auth-switch"
    >
      <button
        type="button"
        class="fair-auth-form__link"
        @click="authModal.showAuthModal('login')"
      >
        {{ t('account.restore.back_to_login') }}
      </button>
    </p>
  </AuthScreenShell>
</template>
